import appConfig from '../../config/appConfig';
import {getDeviceTypesData} from '../../consts/device/deviceTypesData';
import {IDeviceDataTypeOptions} from '../../consts/device/deviceTypesData';
import DEVICE_TYPES from '../../enums/device/deviceTypes';

type TGetDeviceModelByType = (type: string) => string | undefined;
type TIsEqualPlatform = (type: DEVICE_TYPES) => boolean | undefined;
type TGetProductNameKeyByType = (type?: string) => string | undefined;
type TIsEqualType = (type: string) => boolean | undefined;
type TGetDevicePlatform = (deviceType: string) => string | null;
type TIsDeviceBleDisabledByType = (type: string) => boolean | undefined;
type TConfigurationCode = {
    configurationCodeByte0: number;
    configurationCodeByte1: number;
    configurationCodeByte2: number;
    configurationCodeByte3: number;
};
type TMaterial = {
    materialBrandFamily?: string;
    materialColor?: string;
    materialGroup?: string;
    materialModelNumber?: string;
};

const getDeviceTypesDataByType = (type: string): IDeviceDataTypeOptions | null => {
    if (!type) return null;

    const result = getDeviceTypesData()[type as DEVICE_TYPES];

    return result ? result : null;
};

const getDeviceModelByType: TGetDeviceModelByType = (type) => getDeviceTypesDataByType(type)?.deviceModel;
const getProductNameKeyByType: TGetProductNameKeyByType = (type) =>
    type ? getDeviceTypesDataByType(type)?.nameKey : undefined;
const isCortez: TIsEqualType = (type) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V30_CHARGER;
const isCosmos: TIsEqualType = (type) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V40C_CHARGER;
const isOrdinary: TIsEqualType = (type) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_V24P_CHARGER;
const isP1: TIsEqualPlatform = (type) => getDeviceTypesDataByType(type)?.type === DEVICE_TYPES.DEV_TYPE_P1;
const isP4: TIsEqualPlatform = (type) => getDeviceTypesDataByType(type)?.type === DEVICE_TYPES.DEV_TYPE_P4;
const isP1V4: TIsEqualPlatform = (type) => getDeviceTypesDataByType(type)?.type === DEVICE_TYPES.DEV_TYPE_P1_V4;
const isP1V41 = (type: string) => isHermes(type) || isHermesHolder(type) || isJuno(type);
const isHermesHolder = (type: string) => isHermesMidHolder(type) || isHermesPrimeHolder(type);
const isHermesMidHolder = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41M_HOLDER_CONTROL;
const isHermesPrimeHolder = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41P_HOLDER_CONTROL;
const isSoyuz: TIsEqualType = (type) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER;
const isAriane: TIsEqualType = (type) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1_ARIANE;
const isVespucci: TIsEqualType = (type) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V30M;
const isOriginalOne: TIsEqualType = (type) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V31M;
const isOriginalDuo: TIsEqualType = (type) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER;
const isVoyager: TIsEqualType = (type) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V40V_CHARGER;
const isDelta: TIsEqualType = (type) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V40D_CONTROL;
const isHermesPrime = (type: string) =>
    getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41P_CHARGER;
const isHermesMid = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41M_CHARGER;
const isJuno = (type: string) => getDeviceTypesDataByType(type)?.fullType === DEVICE_TYPES.DEV_TYPE_P1V41J_CONTROL;
const isHermes = (type: string) => isHermesMid(type) || isHermesPrime(type);
const isMono: TIsEqualType = (type) => getDeviceTypesDataByType(type)?.isMono;
const isIluma: TIsEqualType = (type) => isCosmos(type) || isVoyager(type) || isDelta(type);
const isOriginalsDuoDeviceByCodeOrMaterial = ({
    configurationCode,
    material,
    isHolder,
}: {
    configurationCode?: TConfigurationCode;
    material?: TMaterial;
    isHolder?: boolean;
}): boolean | undefined => {
    const comparedByte = isHolder ? 2 : 1; //regional configuration(1 - original one charger, 2 - original one holder)

    if (configurationCode) {
        const isOriginalsDuoByCode = configurationCode.configurationCodeByte1 === comparedByte;

        return isOriginalsDuoByCode;
    }

    const {materialBrandFamily} = material || {};

    if (materialBrandFamily) {
        const isOriginalsDuoByMaterial = materialBrandFamily === 'IO';

        return isOriginalsDuoByMaterial;
    }
};

const getDevicePlatform: TGetDevicePlatform = (deviceType) => {
    const type = getDeviceTypesDataByType(deviceType)?.type;

    if (!type) return null;

    switch (type) {
        case DEVICE_TYPES.DEV_TYPE_P1:
            return DEVICE_TYPES.DEV_TYPE_P1;
        case DEVICE_TYPES.DEV_TYPE_P4:
            return DEVICE_TYPES.DEV_TYPE_P4;
        case DEVICE_TYPES.DEV_TYPE_P1_V4:
            return DEVICE_TYPES.DEV_TYPE_P1_V4;
        default:
            return null;
    }
};

const isDeviceBleDisabledByType: TIsDeviceBleDisabledByType = (type: string) => {
    const bleDisabledForDevice: string[] = appConfig.getBleDisabledForDevice();

    return bleDisabledForDevice.includes(type);
};

export default {
    getDeviceModelByType,
    getDevicePlatform,
    getProductNameKeyByType,
    isCortez,
    isCosmos,
    isDelta,
    isMono,
    isOrdinary,
    isOriginalOne,
    isOriginalDuo,
    isP1,
    isP1V4,
    isP1V41,
    isP4,
    isAriane,
    isSoyuz,
    isVespucci,
    isVoyager,
    getDeviceTypesDataByType,
    isIluma,
    isDeviceBleDisabledByType,
    isOriginalsDuoDeviceByCodeOrMaterial,
    isHermesPrime,
    isHermesMid,
    isHermes,
    isJuno,
};
